import React, { useEffect, useState } from 'react';

import { Toaster } from 'components/ui/sonner';
import { useModal } from 'context/ModalContext';
import { captureReferralVisit, postReferralClick } from 'entities/referral';
// import { useGlobalSurveyTracking } from 'features/survey/hooks/useGlobalSurveyTracking';
// import { GlobalSurvey } from 'features/survey/ui/GlobalSurvey';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { REFERRAL_REWARDS_ENABLED } from 'shared/constants';
import { useProtectedPath } from 'shared/hooks/useProtectedPath';
import { sha256 } from 'shared/lib';
import { ChannelService } from 'shared/lib';
import { useInfoStore } from 'store/useInfoStore';

import AppContent from './AppContent';

const DefaultLayout = () => {
  const { search } = useLocation();
  const { email } = useInfoStore();

  const { isProtectedPath, isLogin } = useProtectedPath();

  const { openModal, MODAL_TYPES } = useModal();

  // Tracking: 30s delay for guests; immediate for logged-in users
  // useGlobalSurveyTracking({
  //   timeThreshold: isLogin ? undefined : 30_000,
  // });

  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const redirectPath = urlParams.get('redirect');
    const signupParam = urlParams.get('signup');
    const referralCode = urlParams.get('ref');

    // ?ref= 추천 링크 어트리뷰션 캡처: 30일 저장 + 클릭 집계 (30분 내 동일 코드 재방문은 중복 집계 제외).
    // 길이 제한(코드 20자·채널 30자)은 캡처가 판정 — 초과 코드는 버려지고 채널은 잘려서 반환된다
    if (REFERRAL_REWARDS_ENABLED && referralCode) {
      const { shouldRecordClick, channel } = captureReferralVisit(referralCode, urlParams.get('channel'));
      if (shouldRecordClick) {
        // 집계 실패가 페이지 동작을 막으면 안 됨 — 유효하지 않은 코드는 서버가 조용히 무시
        postReferralClick({ code: referralCode, channel }).catch(() => undefined);
      }
    }

    // signup 파라미터가 있으면 회원가입 모달 표시
    if (signupParam === 'true' && !isLogin) {
      openModal(MODAL_TYPES.SIGNUP);
      return;
    }

    // redirect 파라미터가 있거나 보호된 경로이면서 로그인하지 않은 경우 로그인 모달 표시
    if ((redirectPath || isProtectedPath) && !isLogin) {
      openModal(MODAL_TYPES.LOGIN);
    }
  }, [isLogin, isProtectedPath, search, MODAL_TYPES, openModal]);

  // sha256이 비동기(Web Crypto)로 바뀌어 해시를 state로 관리.
  // 기존과 동일하게 user_id push가 GTM 로드보다 먼저 실행되도록, 해시가 준비된 뒤에 스크립트를 주입한다.
  const [gtmUserId, setGtmUserId] = useState<string | null>(null);

  useEffect(() => {
    if (isLogin && email) {
      // 해시 실패 시(비보안 컨텍스트 등) user_id 없이 GTM은 정상 로드
      sha256(email)
        .then(setGtmUserId)
        .catch(() => setGtmUserId(''));
    } else {
      setGtmUserId('');
    }
  }, [isLogin, email]);

  const getGTMScript = () => {
    const baseScript = `
      window.dataLayer = window.dataLayer || [];
      ${
        gtmUserId
          ? `dataLayer.push({
              'user_id': '${gtmUserId}'
            });`
          : ''
      }
    `;

    const gtmScript = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-T76Z9XGF');
    `;

    return { baseScript, gtmScript };
  };

  const { baseScript, gtmScript } = getGTMScript();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if ((e.target as HTMLElement).tagName === 'IMG') {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, []);

  // 채널톡 메신저 열림 상태 — 모달 밖 클릭으로 닫기 위해 추적
  const [isMessengerOpen, setIsMessengerOpen] = useState(false);

  /**
   * @description
   * 채널톡 스크립트 로드.
   * - hideChannelButtonOnBoot: 기본 launcher 숨김
   * - customLauncherSelector: 우리 버튼을 launcher 로 등록 (클릭 시 자동 메신저 오픈)
   * 메신저는 자체 X 버튼 또는 모달 밖 클릭으로 닫힘.
   */
  useEffect(() => {
    ChannelService.loadScript();

    ChannelService.boot({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      pluginKey: process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY!,
      hideChannelButtonOnBoot: true,
      customLauncherSelector: '#custom-channel-launcher',
    });

    ChannelService.onShowMessenger(() => setIsMessengerOpen(true));
    ChannelService.onHideMessenger(() => setIsMessengerOpen(false));
  }, []);

  // 메신저 열려있을 때 외부 클릭 → 닫기 (모바일/데스크탑 공통)
  useEffect(() => {
    if (!isMessengerOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const messenger = document.querySelector('#ch-plugin');
      const launcher = document.querySelector('#custom-channel-launcher');
      const target = e.target as Node;

      // 메신저 또는 launcher 내부 클릭이면 닫지 않음
      if (messenger?.contains(target) || launcher?.contains(target)) return;

      ChannelService.hideMessenger();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMessengerOpen]);

  return (
    <>
      {/* ⚠️ 스크립트 전용 — meta/title 은 여기 추가 금지.
          react-helmet 은 React 18 동시성 레이스로 meta 태그가 유실됨 (SeoMetaTags.tsx 참고) */}
      {gtmUserId !== null && (
        <Helmet>
          <script>{baseScript}</script>
          {/* Google Tag Manager */}
          <script>{gtmScript}</script>
          {/* End Google Tag Manager */}
        </Helmet>
      )}

      <AppContent />

      {/* expand: 토스트 여러 개일 때 뒤로 겹쳐 쌓지 않고 위아래로 펼친 채 유지 */}
      <Toaster expand />

      {/* 채널톡 커스텀 런처 — 우하단 고정 (모바일 탭바 위, 데스크톱은 기본 위치).
          customLauncherSelector 로 등록되어 클릭 시 ChannelTalk SDK 가 메신저 오픈.
          onClick 은 SDK 지연 로딩 보험 — 스크립트가 아직 안 왔을 때 누르면 selector
          바인딩이 없어 무반응이 되는데, 스텁 큐에 showMessenger 를 쌓아 로드 직후
          열리게 한다. (열려 있을 땐 selector 의 토글 닫기를 방해하지 않도록 스킵) */}
      <button
        id="custom-channel-launcher"
        type="button"
        className="fixed bottom-[80px] right-4 z-30 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-primary shadow-[0_4px_12px_rgba(0,0,0,0.25)] transition-transform hover:scale-105 md:bottom-6 md:right-6"
        aria-label="채널톡 문의"
        onClick={() => {
          if (!isMessengerOpen) ChannelService.showMessenger();
        }}
      >
        <img src="/images/optimized/checkmate_symbol_white.webp" alt="Checkmate" className="h-8 w-8 object-contain" />
      </button>

      {/* 전역 설문 모달 */}
      {/* <GlobalSurvey /> */}
    </>
  );
};

export default DefaultLayout;
